import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { AuthModal } from './AuthModal';
import { UsernamePromptModal } from './UsernamePromptModal';
import { DeleteAccountModal } from './DeleteAccountModal';
import styles from './LoginButton.module.css';

export function LoginButton() {
  const { user, profile, needsUsername, signOut, loading } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [isInitialUsernameSetup, setIsInitialUsernameSetup] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Show username prompt if user is authenticated but doesn't have a profile
  useEffect(() => {
    if (user && needsUsername) {
      setIsInitialUsernameSetup(true);
      setShowUsernameModal(true);
    }
  }, [user, needsUsername]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  if (loading) {
    return <span className={styles.loading}>...</span>;
  }

  if (user) {
    return (
      <>
        <div className={styles.userSection} ref={menuRef}>
          <button
            className={styles.usernameButton}
            onClick={() => setShowMenu(!showMenu)}
          >
            {profile?.username || 'Set username'}
          </button>

          {showMenu && (
            <div className={styles.menu}>
              <button
                className={styles.menuItem}
                onClick={() => {
                  setShowMenu(false);
                  setIsInitialUsernameSetup(false);
                  setShowUsernameModal(true);
                }}
              >
                Change Username
              </button>
              <button
                className={`${styles.menuItem} ${styles.menuItemDanger}`}
                onClick={() => {
                  setShowMenu(false);
                  setShowDeleteModal(true);
                }}
              >
                Delete Account
              </button>
              <div className={styles.menuDivider} />
              <button
                className={styles.menuItem}
                onClick={() => {
                  setShowMenu(false);
                  signOut();
                }}
              >
                Sign Out
              </button>
            </div>
          )}
        </div>

        <UsernamePromptModal
          isOpen={showUsernameModal}
          onComplete={() => setShowUsernameModal(false)}
          onCancel={() => setShowUsernameModal(false)}
          isInitialSetup={isInitialUsernameSetup}
        />

        <DeleteAccountModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
        />
      </>
    );
  }

  return (
    <>
      <button onClick={() => setShowAuthModal(true)} className={styles.button}>
        Sign In
      </button>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="signin"
      />
    </>
  );
}
